import { Box, Link, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import theme from 'App.theme';
import Dialog from 'components/Dialog';
import PinPad, { PIN_LENGTH } from './PinPad';

type Props = {
  open: boolean;
  title: string;
  description?: string;
  // resolves to an error message to show, or undefined when the PIN was accepted
  onSubmit: (pin: string) => Promise<string | undefined>;
  onCancel: VoidFunction;
  secondaryLabel?: string;
  onSecondary?: VoidFunction;
  // when set, the pad is disabled and this is shown in place of any error
  disabledMessage?: string;
};

// PIN pad in a dialog; submits automatically once all digits are entered
const PinPadDialog: FC<Props> = ({
  open,
  title,
  description,
  onSubmit,
  onCancel,
  secondaryLabel,
  onSecondary,
  disabledMessage,
}) => {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (open) {
      setPin('');
      setError(undefined);
    }
  }, [open]);

  const submit = useCallback(
    async (value: string) => {
      setBusy(true);
      try {
        setError(await onSubmit(value));
      } finally {
        setPin('');
        setBusy(false);
      }
    },
    [onSubmit],
  );

  const onChange = useCallback(
    (value: string) => {
      setPin(value);
      setError(undefined);
      if (value.length === PIN_LENGTH) submit(value);
    },
    [submit],
  );

  return (
    <Dialog
      open={open}
      title={title}
      onCancel={onCancel}
      onClose={onCancel}
      body={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.layout.spacing.medium }}>
          {description && <Typography align="center">{description}</Typography>}
          <PinPad value={pin} onChange={onChange} disabled={busy || !!disabledMessage} />
          <Typography color="error" minHeight="1.5em" role="alert">
            {disabledMessage ?? error}
          </Typography>
          {secondaryLabel && onSecondary && (
            <Link component="button" type="button" onClick={onSecondary} disabled={busy}>
              {secondaryLabel}
            </Link>
          )}
        </Box>
      }
    />
  );
};

export default PinPadDialog;
